/**
 * The change-card form (TMMAPP-0015; shared since TMMAPP-0119 item 11): the card capture, then
 * `PUT /v1/me/billing/payment-method` with the single-use `cardToken` and a fresh Idempotency-Key.
 * Settings → Billing hosts it in its change-card dialog; a wallet charge refused for want of the
 * card's billing address hosts it in place (DLG-005), then retries the charge. Nothing about the card
 * is logged or stored.
 *
 * Busy runs from the press (tokenize) to the PUT's answer (TMMAPP-0122 item 25), so a second click
 * during tokenize sends nothing and the host holds its close the whole time.
 */
import { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { billingApi, type PaymentMethodResult } from '@/api/billing';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ReferenceLine } from '@/components/ReferenceLine';
import { generateRequestId } from '@/utils/requestId';
import { CardCapture } from './CardCapture';

interface ChangeCardFormProps {
  /** The card was updated: the host closes, refetches, or retries what needed it. */
  onUpdated: (result: PaymentMethodResult) => void | Promise<void>;
  onCompleteChange?: (complete: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
  /** The update failed: the host may move focus to the error. */
  onFailed?: () => void;
}

export function ChangeCardForm({
  onUpdated,
  onCompleteChange,
  onBusyChange,
  onFailed,
}: ChangeCardFormProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<{ message: string; requestId: string | null } | null>(null);

  const handleSubmitting = (submitting: boolean) => {
    setBusy(submitting);
    onBusyChange?.(submitting);
  };

  async function handleCardToken(cardToken: string) {
    setFailed(null);
    try {
      const result = await billingApi.paymentMethod({
        cardToken,
        idempotencyKey: generateRequestId(),
      });
      await onUpdated(result);
    } catch (error) {
      // A setup problem on our side (TMMAPI-0159) says so, with its Reference line; anything else
      // is the generic sentence.
      setFailed(
        isApiError(error) && error.code === ErrorCodes.BILLING_CONFIGURATION
          ? { message: t('billing.configurationError'), requestId: error.requestId ?? null }
          : { message: t('billing.errorCardUpdate'), requestId: null },
      );
      onFailed?.();
    }
  }

  return (
    <Box data-change-card-form="">
      {failed !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {failed.message}
          {failed.requestId !== null && (
            <ReferenceLine requestId={failed.requestId} ink="inherit" />
          )}
        </Alert>
      )}
      <CardCapture
        submitLabel={t('billing.changeCard')}
        busy={busy}
        {...(onCompleteChange !== undefined ? { onCompleteChange } : {})}
        onSubmittingChange={handleSubmitting}
        onToken={handleCardToken}
      />
    </Box>
  );
}
